/**
 * Draft state of the Properties panel (review C F2 / F3). Each section — metadata, HTTP headers, index tags — keeps its
 * own baseline (the server value its draft started from) and the ETag of that baseline:
 * - a clean section adopts every refresh;
 * - a dirty section whose server value still equals its baseline advances its ETag (the change was elsewhere; saving
 *   this section replaces only this section, so nothing is lost);
 * - a dirty section whose server value now equals its draft adopts it (typically this panel's own save landing);
 * - otherwise the section keeps its baseline and ETag and is in CONFLICT: Save stays disabled until Reload, and a
 *   save that raced the refresh is still conditioned on the old ETag, so Azure refuses it.
 *
 * Final UI/UX batch C1 review (C1-01): a section save records what it sent and the section's edit revision at dispatch
 * (`dispatch`). When the refresh or the acknowledgement shows that value landed and the section was edited AFTER the
 * dispatch, the landed value becomes the baseline (with its ETag) and the newer edit stays as a draft against it — even
 * an edit back to the original baseline, which looked clean until the write landed (Cancel during the guard's Save).
 */
import type { StorageBlobHttpHeaders, StorageResourceProperties } from '../../../../types/azureStorage';
import { equalHeaders, equalStringMap, headersFromProperties, mapFromRows, rowsFromMap, type KeyValueRow, type RowsResult } from './propertyDrafts';

export type DraftSection = 'metadata' | 'headers' | 'tags';
type MapSection = { rows: KeyValueRow[]; baseline: Record<string, string>; etag?: string };
type HeaderSection = { draft: StorageBlobHttpHeaders; baseline: StorageBlobHttpHeaders; etag?: string };
export type SectionValue = Record<string, string> | StorageBlobHttpHeaders;
/** A section write on its way (C1-01): the value sent and the section's edit revision when it was sent. */
type PendingSave = { value: SectionValue; revision: number };
/**
 * `revision` counts user edits (rows / headers) — a reload discards only the revision it began with (UXA-02 r2);
 * `sectionRevisions` counts them per section and `pending` holds each section's write in flight (C1-01).
 */
export type PropertiesDraftState = {
  resourceKey?: string; server?: StorageResourceProperties; metadata: MapSection; headers: HeaderSection; tags: MapSection; revision?: number;
  sectionRevisions?: Partial<Record<DraftSection, number>>; pending?: Partial<Record<DraftSection, PendingSave>>;
};
export type PropertiesDraftAction =
  | { type: 'server'; resourceKey: string; value: StorageResourceProperties }
  | { type: 'rows'; section: 'metadata' | 'tags'; rows: KeyValueRow[] }
  | { type: 'headers'; headers: StorageBlobHttpHeaders }
  | { type: 'dispatch'; section: DraftSection; value: SectionValue }
  | { type: 'failed'; section: DraftSection; value: SectionValue }
  | { type: 'saved'; section: DraftSection; value: SectionValue; reread?: StorageResourceProperties }
  | { type: 'discard'; section?: DraftSection };

export const METADATA_RULES = { caseInsensitiveKeys: true } as const;
export const TAG_RULES = { caseInsensitiveKeys: false } as const;

export const EMPTY_PROPERTIES_DRAFT: PropertiesDraftState = {
  metadata: { rows: [], baseline: {} }, headers: { draft: {}, baseline: {} }, tags: { rows: [], baseline: {} },
};

const etagOf = (value: StorageResourceProperties): string | undefined => (typeof value.properties.etag === 'string' ? value.properties.etag : undefined);

/**
 * `landed` (C1-01): the value this section's write sent, when the section was edited after sending it. The server holding
 * it means the write landed: it becomes the baseline and the newer edit is kept as a draft against it.
 */
function reconcileMap(section: MapSection, server: Record<string, string> | undefined, etag: string | undefined, fold: boolean, landed?: Record<string, string>): MapSection {
  if (!server) return section;
  if (landed) {
    if (equalStringMap(server, landed, fold)) return { rows: section.rows, baseline: server, etag };
    return equalStringMap(server, section.baseline, fold) ? { ...section, etag } : section;
  }
  const draft = mapFromRows(section.rows, { caseInsensitiveKeys: fold });
  const dirty = !draft.ok || !equalStringMap(draft.value, section.baseline, fold);
  if (!dirty || (draft.ok && equalStringMap(draft.value, server, fold))) return { rows: dirty ? section.rows : rowsFromMap(server, section.rows), baseline: server, etag };
  return equalStringMap(server, section.baseline, fold) ? { ...section, etag } : section;
}

function reconcileHeaders(section: HeaderSection, server: StorageBlobHttpHeaders, etag: string | undefined, landed?: StorageBlobHttpHeaders): HeaderSection {
  if (landed) {
    if (equalHeaders(server, landed)) return { draft: section.draft, baseline: server, etag };
    return equalHeaders(server, section.baseline) ? { ...section, etag } : section;
  }
  const dirty = !equalHeaders(section.draft, section.baseline);
  if (!dirty || equalHeaders(section.draft, server)) return { draft: dirty ? section.draft : server, baseline: server, etag };
  return equalHeaders(server, section.baseline) ? { ...section, etag } : section;
}

/** The value a section's pending write sent, if the section was edited since (C1-01); undefined otherwise. */
function landedEdit(state: PropertiesDraftState, section: DraftSection): SectionValue | undefined {
  const pending = state.pending?.[section];
  return pending && (state.sectionRevisions?.[section] ?? 0) > pending.revision ? pending.value : undefined;
}

function reconcile(state: PropertiesDraftState, value: StorageResourceProperties): PropertiesDraftState {
  const etag = etagOf(value);
  return {
    ...state,
    server: value,
    metadata: reconcileMap(state.metadata, value.metadata, etag, true, landedEdit(state, 'metadata') as Record<string, string> | undefined),
    headers: reconcileHeaders(state.headers, headersFromProperties(value), etag, landedEdit(state, 'headers') as StorageBlobHttpHeaders | undefined),
    tags: reconcileMap(state.tags, value.tags, etag, false, landedEdit(state, 'tags') as Record<string, string> | undefined),
  };
}

const bumped = (state: PropertiesDraftState, section: DraftSection): Partial<Record<DraftSection, number>> =>
  ({ ...state.sectionRevisions, [section]: (state.sectionRevisions?.[section] ?? 0) + 1 });

function withoutPending(state: PropertiesDraftState, section: DraftSection): PropertiesDraftState {
  if (!state.pending?.[section]) return state;
  const { [section]: _settled, ...pending } = state.pending;
  return { ...state, pending };
}

function fresh(resourceKey: string, value: StorageResourceProperties): PropertiesDraftState {
  const etag = etagOf(value);
  const headers = headersFromProperties(value);
  const tags = value.tags ?? {};
  return {
    resourceKey, server: value,
    metadata: { rows: rowsFromMap(value.metadata), baseline: value.metadata, etag },
    headers: { draft: headers, baseline: headers, etag },
    tags: { rows: rowsFromMap(tags), baseline: tags, etag },
  };
}

export function propertiesDraftReducer(state: PropertiesDraftState, action: PropertiesDraftAction): PropertiesDraftState {
  switch (action.type) {
    case 'server':
      return state.resourceKey === action.resourceKey ? reconcile(state, action.value) : fresh(action.resourceKey, action.value);
    case 'rows':
      return { ...state, revision: (state.revision ?? 0) + 1, sectionRevisions: bumped(state, action.section), [action.section]: { ...state[action.section], rows: action.rows } };
    case 'headers':
      return { ...state, revision: (state.revision ?? 0) + 1, sectionRevisions: bumped(state, 'headers'), headers: { ...state.headers, draft: action.headers } };
    case 'dispatch':
      return { ...state, pending: { ...state.pending, [action.section]: { value: action.value, revision: state.sectionRevisions?.[action.section] ?? 0 } } };
    case 'failed':
      // Only the write that failed: a newer dispatch of the same section (which superseded it) stays pending.
      return state.pending?.[action.section]?.value === action.value ? withoutPending(state, action.section) : state;
    case 'saved': {
      // The write replaced the section; without a re-read its new ETag is unknown, so a blob section needs Reload before
      // its next save. The drafts are left alone: an edit made after the dispatch (C1-01) stays, now against this baseline.
      const settled = withoutPending(state, action.section);
      const saved = action.section === 'headers'
        ? { ...settled, headers: { ...settled.headers, baseline: action.value as StorageBlobHttpHeaders, etag: undefined } }
        : { ...settled, [action.section]: { ...settled[action.section], baseline: action.value as Record<string, string>, etag: undefined } };
      return action.reread ? reconcile(saved, action.reread) : saved;
    }
    case 'discard': {
      if (!state.server || !state.resourceKey) return state;
      const reset = fresh(state.resourceKey, state.server);
      // The revision stays monotonic across a discard, so a reload's "revision it began with" can never recur.
      if (!action.section) return { ...reset, revision: state.revision, sectionRevisions: state.sectionRevisions, pending: state.pending, metadata: { ...reset.metadata, rows: rowsFromMap(reset.metadata.baseline, state.metadata.rows) }, tags: { ...reset.tags, rows: rowsFromMap(reset.tags.baseline, state.tags.rows) } };
      const section = action.section === 'headers' ? reset.headers : { ...reset[action.section], rows: rowsFromMap(reset[action.section].baseline, state[action.section].rows) };
      return { ...state, [action.section]: section };
    }
    default:
      return state;
  }
}

export type MapSectionView = { dirty: boolean; conflict: boolean; draft: RowsResult; etag?: string };
export type HeaderSectionView = { dirty: boolean; conflict: boolean; draft: StorageBlobHttpHeaders; etag?: string };

/** Dirty = the draft differs from its baseline; conflict = dirty while the server holds a different value than that baseline. */
export function mapSectionView(state: PropertiesDraftState, section: 'metadata' | 'tags'): MapSectionView {
  const fold = section === 'metadata';
  const current = state[section];
  const draft = mapFromRows(current.rows, { caseInsensitiveKeys: fold });
  const dirty = !draft.ok || !equalStringMap(draft.value, current.baseline, fold);
  const server = section === 'metadata' ? state.server?.metadata : state.server?.tags;
  return { dirty, conflict: dirty && !!server && !equalStringMap(server, current.baseline, fold), draft, etag: current.etag };
}

export function headerSectionView(state: PropertiesDraftState): HeaderSectionView {
  const dirty = !equalHeaders(state.headers.draft, state.headers.baseline);
  const server = state.server ? headersFromProperties(state.server) : undefined;
  return { dirty, conflict: dirty && !!server && !equalHeaders(server, state.headers.baseline), draft: state.headers.draft, etag: state.headers.etag };
}
